import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faTimes } from "@fortawesome/pro-solid-svg-icons/faTimes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useId } from "react";
import { DreamMuiDialog } from "@portal/app/_components/ui-elements/Mui/DreamMuiDialog";

type ButtonPropsWithTestId = React.ComponentProps<typeof Button> & {
  "data-testid"?: string;
};

export const ConfirmationDialog = ({
  onClose,
  title = "Are you sure?",
  content = "Please confirm you'd like to take this action.",
  confirmButtonProps: { children: confirmButtonChildren = "Confirm", ...propsToConfirmButton } = {},
  cancelButtonProps: { children: cancelButtonChildren = "Cancel", ...propsToCancelButton } = {},
  ...propsToDialog
}: Omit<React.ComponentProps<typeof Dialog>, "onClose" | "content"> & {
  title?: React.ReactNode;
  content?: React.ReactNode;
  /**
   * Callback to return True if the user confirms, and False if they cancel.
   * Note: This is a non-standard override for Dialog's native `onClose`
   * behavior.
   */
  onClose: (didUserConfirm: boolean) => void | Promise<unknown>;
  confirmButtonProps?: ButtonPropsWithTestId;
  cancelButtonProps?: ButtonPropsWithTestId;
}) => {
  const titleId = useId();

  return (
    <DreamMuiDialog
      {...propsToDialog}
      aria-labelledby={titleId}
      // This handles Esc key and backdrop clicks as Cancel
      onClose={() => onClose(false)}
    >
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        <Button
          onClick={() => onClose(false)}
          color="secondary"
          startIcon={<FontAwesomeIcon icon={faTimes} />}
          data-testid="confirm-cancel"
          {...propsToCancelButton}
        >
          {cancelButtonChildren}
        </Button>
        <Button
          onClick={() => onClose(true)}
          color="primary"
          startIcon={<FontAwesomeIcon icon={faCheck} />}
          data-testid="confirm-ok"
          autoFocus
          {...propsToConfirmButton}
        >
          {confirmButtonChildren}
        </Button>
      </DialogActions>
    </DreamMuiDialog>
  );
};

export const DialogActionButton = (props: ButtonPropsWithTestId) => (
  <Button sx={{ px: 3 }} {...props} />
);
